import React, { Suspense, ComponentType } from "react";
import Spinner from "./Spinner";

const RELOAD_GUARD_KEY = "chunk-load-reload-attempted";

const isChunkLoadError = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string } | null;
  return (
    err?.name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(err?.message || "")
  );
};

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

// A lazy-loaded route's chunk hash can go stale between page load and the
// moment it's actually navigated to — a new QA deploy replaces the built
// files, so the browser's already-cached index.html points at a chunk
// filename that no longer exists on the server. That's a stale asset, not a
// real bug, so recover from it with a single automatic reload (which fetches
// the current index.html/chunk manifest) instead of surfacing the router's
// raw "Unexpected Application Error" screen. Guarded via sessionStorage so a
// chunk that's missing for a real reason doesn't reload forever.
class ChunkErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
      window.location.reload();
      return { hasError: false };
    }
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const WithSuspense = <P extends object>(Component: ComponentType<P>) => {
  const WrappedComponent: React.FC<P> = (props: P) => (
    <ChunkErrorBoundary>
      <Suspense fallback={<div className="flex-1 min-h-[70vh] flex items-center justify-center"><Spinner /></div>}>
        <Component {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );

  WrappedComponent.displayName = `WithSuspense(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
};

export default WithSuspense;
